<template>
   <div id="my-active-tasks">
      <div class="group-page-header">
         <h1 class="group-page-title">{{ $t("groupLayout_myActiveRequests") }}</h1>
         <p class="group-page-subtitle">{{ $t("taskRequestList_subtitle") }}</p>
      </div>

      <div v-if="activeTasks.length === 0" class="empty-state">
         <p>{{ $t("taskRequestList_noTasksFound") }}</p>
      </div>

      <div v-else class="tasks-list">
         <div v-for="(task, index) in activeTasks" :key="index" class="task-item" @click="goToTask(task)">
            <div class="task-left">
               <div class="task-content">
                  <div class="task-name">{{ task.groupPnrDetails.groupName }}</div>
                  <div class="task-info">
                     <span class="info-item"> {{ task.groupPnrDetails.departureAirport }} → {{ task.groupPnrDetails.arrivalAirport }} </span>
                     <span class="separator">•</span>
                     <span class="info-item">
                        {{ task.groupPnrDetails.departureDate | FormatterDate }}
                     </span>
                     <span class="separator">•</span>
                     <span class="info-item">
                        {{ task.groupPnrDetails.paxCounts.ADULT + task.groupPnrDetails.paxCounts.CHILD + task.groupPnrDetails.paxCounts.INFANT }} {{ $t("common_passenger") }}
                     </span>
                  </div>
                  <div v-if="task.lastMessage" class="task-message">{{ task.lastMessage.text }}</div>
               </div>
            </div>
            <div class="task-right">
               <div class="gts-badge small" :class="'ETaskSubStatus-' + task.subStatus">{{ getTaskStatusLabel(task.subStatus) }}</div>
               <i class="gts-icon icon-arrow-right icon-xs"></i>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { getTaskStatusLabel } from "@/composables/useTaskStatus.js";
import { useRecentTasks } from "@/composables/useRecentTasks.js";

export default {
   name: "MyActiveTasks",
   data() {
      return {
         activeTasks: [],
      };
   },
   methods: {
      loadTasksFromCookie() {
         const { getRecentTasks } = useRecentTasks();
         this.activeTasks = getRecentTasks();

         // If only one task, redirect to it immediately
         if (this.activeTasks.length === 1) {
            this.goToTask(this.activeTasks[0]);
         }
      },
      goToTask(task) {
         this.$router.push(task.path);
      },
      getTaskStatusLabel(subStatus) {
         return getTaskStatusLabel(subStatus, (key) => this.$i18n.t(key));
      },
   },
   mounted() {
      this.loadTasksFromCookie();
   },
};
</script>

<style lang="scss" scoped>
#my-active-tasks {
   .empty-state {
      padding: 60px 20px;
      text-align: center;
      color: var(--gray-500);
      font-size: 14px;
   }

   .tasks-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
   }

   .task-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 20px;
      background: var(--surface-level-1);
      border: 1px solid var(--border-default);
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
         border-color: var(--accent-primary);
         box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
   }

   .task-left {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      flex: 1;
      min-width: 0;
   }

   .task-avatar {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      background: var(--blue-50);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .gts-icon {
         font-size: 24px;
         color: var(--blue-600);
      }
   }

   .task-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 0;
   }

   .task-name {
      font-size: 16px;
      font-weight: 600;
      color: var(--blue-600);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }

   .task-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .gts-badge.small {
         font-size: 10px;
         padding: 4px 10px;
      }

      .gts-icon {
         color: var(--gray-400);
      }
   }

   .task-info {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .info-item {
         display: flex;
         align-items: center;
         font-size: 13px;
         color: var(--gray-600);
      }

      .separator {
         font-size: 25px;
         color: var(--gray-800);
      }
   }

   .task-message {
      font-size: 12px;
      color: var(--gray-500);
      font-style: italic;
      line-height: 1.4;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }
}
</style>
